import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { articles } from "@/mocks/articles";
import type { Article } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    const found = articles.find((a) => a.id === id);
    setArticle(found || null);

    if (found) {
      document.title = `${found.title} | 爱看影院`;

      const related = articles
        .filter((a) => a.id !== id)
        .filter((a) => a.tags.some((t) => found.tags.includes(t)))
        .slice(0, 4);
      setRelatedArticles(related);
    }
  }, [id]);

  if (!article) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-800/50 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-300">文章未找到</h2>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://aikan.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "影视资讯",
                item: "https://aikan.com/article/article-1",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            author: {
              "@type": "Person",
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.coverImage,
            url: `https://aikan.com/article/${article.id}`,
          }),
        }}
      />

      <article className="w-full px-4 md:px-6 py-8 md:py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
            首页
          </Link>
          <span>/</span>
          <Link to="/category/movie" className="hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
            影视资讯
          </Link>
          <span>/</span>
          <span className="text-foreground-300 line-clamp-1">{article.title}</span>
        </nav>

        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500/15 text-primary-300 border border-primary-500/20"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-50 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-800/50">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </div>
              {article.views > 10000
                ? `${(article.views / 10000).toFixed(1)}万`
                : article.views}{" "}
              阅读
            </span>
          </div>

          <div className="relative h-56 md:h-80 rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-200 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-800/50">
              <h3 className="text-lg font-bold font-heading text-foreground-100 mb-5">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="flex gap-4 p-4 rounded-xl bg-background-900/60 border border-background-800/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                  >
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground-100 line-clamp-2 hover:text-primary-400 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">
                        {ra.publishDate} · {ra.author}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <style>{`
        .prose-custom h2 {
          font-size: 1.375rem;
          font-weight: 700;
          font-family: var(--font-heading);
          color: oklch(var(--foreground-100));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          padding-left: 0.75rem;
          border-left: 3px solid oklch(var(--primary-500));
        }
        .prose-custom p {
          margin-bottom: 1rem;
          color: oklch(var(--foreground-300));
          line-height: 1.85;
          font-size: 0.9375rem;
        }
      `}</style>
    </Layout>
  );
}